import type { AgencyDto } from 'shared/dtos/agencies.dto';
import type {
  AgenciesState,
  AgencyFormFormikState,
} from 'shared/interfaces/agencies.interface';
import type { DivisionsState } from 'shared/interfaces/divisions.interface';
import type { PeopleState } from 'shared/interfaces/people.interface';

import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Button } from 'components/buttons/button/button';
import { CreateButton } from 'components/buttons/create-button/create-button';
import { InputField } from 'components/fields/input-field/input-field';
import { TextEditor } from 'components/fields/text-editor/text-editor';
import { AssignedDivisionsList } from 'components/lists/assigned-divisions-list/assigned-divisions-list';
import { GrayPanel } from 'components/panels/gray-panel/gray-panel';
import { Container } from 'components/template/container/container';
import { FixedHeader } from 'components/template/fixed-header/fixed-header';
import { FormBody } from 'components/template/form-body/form-body';
import { FormHeader } from 'components/template/form-header/FormHeader';
import { Spinner } from 'components/template/spinner/spinner';
import { PermissionsContext } from 'contexts';
import { Form, Formik } from 'formik';
import orderBy from 'lodash.orderby';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReportService from 'services/reports.service';
import events from 'shared/constants/events';
import { validateField } from 'shared/utils';
import CheckIfFormIsDirty from 'shared/utils/checkIfFormIsDirty';
import { useAppDispatch, useAppSelector } from 'store';
import {
  clearAgencyIdFromDivision,
  setAgencyDescription,
} from 'store/agencies/agencies.slice';
import { clearAgencyIdFromSelectedDivision } from 'store/divisions/divisions.slice';
import { setFormDirty } from 'store/formIsDirty/formIsDirty.slice';
import { vocabulary } from 'vocabulary/german';

interface AgencyFormProps {
  agenciesState: AgenciesState;
  peopleState: PeopleState;
  divisionsState: DivisionsState;
  onSubmit: (values: AgencyFormFormikState, combinedDivisions: any[]) => void;
  onDelete: (id: number) => void;
  saveToCurrentAgency: (values: AgencyDto) => void;
  openAssignPeopleModal: () => void;
  openAssignDivisionModal: () => void;
}

const reportsService = new ReportService();

export const AgencyForm = ({
  agenciesState,
  peopleState,
  divisionsState,
  onSubmit,
  onDelete,
  saveToCurrentAgency,
  openAssignPeopleModal,
  openAssignDivisionModal,
}: AgencyFormProps) => {
  const dispatch = useAppDispatch();
  const { permissions } = useContext(PermissionsContext);
  const currentYearFrozen = useAppSelector(
    (state) => state.years.currentYearFrozen,
  );
  const [locked, setLocked] = useState<boolean>(false);
  const [focusPosition, setFocusPosition] = useState(0);
  const setFieldValueRef = useRef<
    ((field: string, value: unknown) => void) | null
  >(null);

  const initialValues: AgencyFormFormikState = {
    id: agenciesState.current?.id || null,
    title: agenciesState.current?.title || '',
    description: agenciesState.current?.description || '',
    MidtermGoals: agenciesState.current?.MidtermGoals || [],
    PeopleId: peopleState.selected.forAgency.peopleId
      ? peopleState.selected.forAgency.peopleId
      : agenciesState.current?.PeopleId || null,
    Divisions: agenciesState.current?.Divisions || [],
  };

  const formKey = `${agenciesState.current?.id ?? 'new'}-${peopleState.selected.forAgency.peopleId ?? 'none'}`;

  const renderSaveButton = () => {
    if (permissions.edit === true && !currentYearFrozen) {
      return <Button type="submit" text={vocabulary.buttons.save} />;
    }
  };

  const renderDeleteButton = (id: number | null) => {
    if (!id) {
      return null;
    }

    if (permissions.remove === true && !currentYearFrozen) {
      return (
        <Button
          className={'delete-button'}
          text={vocabulary.buttons.delete}
          onClick={() => onDelete(id)}
        />
      );
    }
  };

  const renderReportButton = (id: number | null, type: 'pdf' | 'docx') => {
    if (!id) {
      return null;
    }

    if (permissions.printReport === true) {
      return (
        <Button
          text={
            type === 'pdf'
              ? vocabulary.buttons.reports.downloadReportPdf
              : vocabulary.buttons.reports.downloadReportDocx
          }
          onClick={() => downloadAgencyReport(id, type)}
        />
      );
    }
  };

  const downloadAgencyReport = (agencyId: number, type: 'pdf' | 'docx') => {
    setLocked(true);
    reportsService.downloadAgencyReport(agencyId, type);
  };

  const renderResponsiblePerson = () => {
    if (peopleState.selected.forAgency.peopleId) {
      return (
        <p className={'agencies__person'}>
          {`${vocabulary.forms.agency.responsiblePerson}`}:
          <span>{getPeopleName(peopleState.selected.forAgency.peopleId)}</span>
        </p>
      );
    }

    if (agenciesState.current && agenciesState.current.PeopleId) {
      return (
        <p className={'agencies__person'}>
          {`${vocabulary.forms.agency.responsiblePerson}`}:{' '}
          <span>{getPeopleName(agenciesState.current.PeopleId)}</span>
        </p>
      );
    }

    return <p className={'agencies__person'}>Kein(e) person zugewiesen.</p>;
  };

  const getPeopleName = (peopleId: number | null) => {
    return (
      peopleState.list.find((person) => person.id === peopleId)?.name ?? ''
    );
  };

  const getCreateButtonText = () => {
    return agenciesState.current && agenciesState.current.PeopleId
      ? vocabulary.buttons.reassignPerson
      : vocabulary.buttons.assignPeople;
  };

  const handleCreate = (values) => {
    saveToCurrentAgency(values);
    openAssignPeopleModal();
  };

  const getDefaultDivisions = useMemo(() => {
    return agenciesState.current?.Divisions || [];
  }, [agenciesState]);

  const getSelectedDivisions = useMemo(() => {
    const selectedDivisions: any = [];

    if (divisionsState.assigned) {
      for (const divisionId of divisionsState.assigned) {
        const division = divisionsState.list.find(
          (division) => division.id === divisionId,
        );

        if (division) {
          selectedDivisions.push({ ...division, needToAdd: true });
        }
      }
    }

    return selectedDivisions;
  }, [divisionsState.assigned, divisionsState.list]);

  const getCombinedDivisions = useMemo(() => {
    return orderBy(
      [...getDefaultDivisions, ...getSelectedDivisions],
      'id',
      'asc',
    );
  }, [getDefaultDivisions, getSelectedDivisions]);

  const handleDelete = (id: number) => {
    const isDefaultDivisionId = getDefaultDivisions.some(
      (division) => division.id === id,
    );
    const isSelectedDivisionId = getSelectedDivisions.some(
      (division) => division.id === id,
    );

    if (isDefaultDivisionId) {
      dispatch(clearAgencyIdFromDivision(id));
    } else if (isSelectedDivisionId) {
      dispatch(clearAgencyIdFromSelectedDivision(id));
    }
  };

  useEffect(() => {
    const unlock = () => setLocked(false);
    document.body.addEventListener(events.UNLOCK.name, unlock);

    return () => {
      document.body.removeEventListener(events.UNLOCK.name, unlock);
    };
  }, []);

  useEffect(() => {
    if (peopleState.selected.forAgency.peopleId) {
      // if we select person or change it, we set form to dirty, to prevent unsaved data
      dispatch(setFormDirty(true));
    }
    if (getCombinedDivisions.some((division) => division.AgencyId === null)) {
      // if we delete division or add new, we set form to dirty, to prevent unsaved data
      dispatch(setFormDirty(true));
    }
  }, [dispatch, getCombinedDivisions, peopleState.selected]);

  const informationEditor = useEditor({
    extensions: [StarterKit],
    content: agenciesState?.current?.description,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      dispatch(setAgencyDescription(html));
      setFieldValueRef.current?.('description', html);
      setFocusPosition(editor.state.selection.from);
    },
  });

  return (
    <Formik
      key={formKey}
      enableReinitialize={false}
      initialValues={initialValues}
      onSubmit={(values) => onSubmit(values, getCombinedDivisions)}
    >
      {({ errors, touched, values, dirty, setFieldValue }) => {
        setFieldValueRef.current = setFieldValue;
        return (
          <Form>
            <CheckIfFormIsDirty dirty={dirty} />
            <FixedHeader>
              <FormHeader
                renderSave={renderSaveButton()}
                renderDelete={renderDeleteButton(initialValues.id)}
                renderReport={renderReportButton(initialValues.id, 'pdf')}
                renderDocxReport={renderReportButton(initialValues.id, 'docx')}
              />
              <Container>
                <div className="field-wrapper">
                  <InputField
                    name="title"
                    className="form__field_big bordered"
                    validate={validateField}
                    placeholder={vocabulary.forms.agency.placeholders.newAgency}
                  />
                  {errors.title && touched.title && (
                    <p className={'field-error'}>{errors.title}</p>
                  )}
                </div>
              </Container>
            </FixedHeader>

            <FormBody>
              <GrayPanel text={vocabulary.forms.agency.description} first />
              <Container>
                <div className="field-wrapper percent-52">
                  <TextEditor
                    position={focusPosition}
                    editor={informationEditor}
                    commentText={values.description || ''}
                  />
                  {errors.description && touched.description && (
                    <p className={'field-error'}>{errors.description}</p>
                  )}
                </div>
              </Container>

              <GrayPanel text={vocabulary.forms.agency.responsiblePerson} />
              <div className="container">
                <div className={'agencies-footer'}>
                  {renderResponsiblePerson()}
                  {permissions.assign === true && (
                    <CreateButton
                      text={getCreateButtonText()}
                      onClick={() => handleCreate(values)}
                    />
                  )}
                </div>
              </div>
              {values.id && (
                <>
                  <GrayPanel
                    text={`${vocabulary.buttons.assignDivisions}, neu von unten`}
                  />
                  <div className="container">
                    <AssignedDivisionsList
                      divisions={getCombinedDivisions}
                      onDelete={handleDelete}
                      newSelectedIds={divisionsState.assigned || []}
                    />
                    {permissions.assign === true && (
                      <CreateButton
                        text={vocabulary.buttons.assignDivisions}
                        onClick={openAssignDivisionModal}
                      />
                    )}
                  </div>
                </>
              )}
            </FormBody>
            {locked && (
              <Spinner
                isFullSize
                message={vocabulary.preloader.waitForReport}
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );
};
